/**
 * Page transitions. Backlog E15, scope.md step 8.
 *
 * FOUR containers. `header` and `#main` are the page. `footer` and
 * `.skip-link` are here because they carry LANGUAGE, which is a11y finding N1
 * of 2026-09-20 and not a transition concern at all.
 *
 * scope.md planned three and put the nav outside them, kept in step by hooks.
 * The nav rides inside <header> instead, which is Ben's decision of
 * 2026-09-19 and cheaper in every direction. The nav is in the document flow
 * of <header> and is painted over the hero only because it is: lifting it out
 * is a layout change on all 36 pages. Leaving it in means the server renders
 * `aria-current` and the language switch's href for the incoming page, and no
 * hook has to know they exist.
 *
 * What the nav costs by riding along is that its markup is replaced on every
 * visit. Between two pages of one language that markup is identical apart
 * from `aria-current`, so nothing moves -- as long as the nav does not
 * ANIMATE. That is why `transition-fade` is on the hero visuals and <main>
 * and deliberately not on the header: `animationSelector` is matched across
 * the whole body and has nothing to do with which elements are containers.
 *
 * WHY THE FOOTER AND THE SKIP LINK JOINED THEM. The language switch goes
 * through Swup, so a visit can cross languages -- and everything outside a
 * container kept the markup of the language the visitor had just left, for
 * the rest of the session. Finding N1 measured six of twelve fields stale
 * after one click, and no further visit repaired any of them. The hook below
 * already copied the two ATTRIBUTES that sit out there; the skip link, the
 * footer's `aria-label`, its seven links and its imprint link are MARKUP, and
 * only a container replaces markup.
 *
 * The same argument the nav makes applies to both: between two pages of one
 * language the markup is identical, so nothing moves, as long as it does not
 * animate. Measured before this was written -- neither carries a
 * `transition-` class, and both are present on all 36 pages in both trees, so
 * neither can be the container Swup finds missing. Both are also strictly
 * SAFER as containers: nav.js's `inert` teardown runs on
 * `before('content:replace')`, so it clears the flags while the old markup is
 * still in the document, and a replaced element cannot carry a stale one.
 *
 * The call button is the one thing out here that a container cannot express.
 * See the reconcile in `content:replace` below for why, and for the two
 * defects it fixes on the way past.
 */
import Swup from 'swup'
import SwupHeadPlugin from '@swup/head-plugin'
import SwupBodyClassPlugin from '@swup/body-class-plugin'
import SwupScrollPlugin from '@swup/scroll-plugin'
import SwupA11yPlugin from '@swup/a11y-plugin'
import SwupPreloadPlugin from '@swup/preload-plugin'
import SwupProgressPlugin from '@swup/progress-plugin'

/* Written by 01_htmlHead from the lexicon, for the same reason the lightbox's
   labels are: the strings are server-side and this runs in the browser. The
   plugin's own defaults are English and would be read out inside a German
   document, which is what a11y finding L1 was about.

   KEYED BY LANGUAGE, not a flat pair, because the language switch now goes
   through Swup. The plugin resolves `announcements[document.documentElement.lang]`
   AT ANNOUNCE TIME -- read from its source, dist/index.modern.js:

       const r = document.documentElement.lang || "*"
       const l = n[r] ?? n["*"] ?? n

   so a keyed object plus a `lang` that travels with the visit is the whole of
   what a cross-language announcement needs. Nothing has to be re-registered
   with the plugin and no option is mutated. */
const announcements = {}

function registerAnnouncements (doc) {
  const lang = doc.documentElement.lang || '*'
  const { announce, announceUrl } = doc.body.dataset
  if (!announce && !announceUrl) return
  announcements[lang] = {
    visit: announce || undefined,
    url: announceUrl || undefined
  }
}

registerAnnouncements(document)

export const swup = new Swup({
  containers: ['header', '#main', 'footer', '.skip-link'],

  plugins: [
    /* persistAssets, or the module script in 04_htmlFooter is re-executed on
       every visit -- a second Swup instance, a second of every listener. */
    new SwupHeadPlugin({ persistAssets: true }),

    /* No prefix, so the whole class attribute is taken from the incoming
       document. That is what carries `page-id-N` across, and it clears
       `nav-open` and `nav-stuck` as a side effect -- both of which SHOULD go,
       since the page they described is gone. nav.js clears the `inert` flags
       that go with `nav-open`; as of 2026-09-20 every element it sets one on
       is reconciled by the visit as well -- the footer and the skip link by
       being containers, the call button by the hook at the bottom of this
       file -- and its teardown is the floor under that rather than the only
       thing holding it up. */
    new SwupBodyClassPlugin(),

    /* The scroll up is ANIMATED, which is the plugin's own default and Ben's
       ask of 2026-09-19. The reference site turns it off and gets the same
       feel from `scroll-behavior: smooth` on <html>; doing it here would put
       the behaviour in two places, and the plugin's version is the one that
       knows a visit is in progress.

       No reduced-motion branch is needed and none should be added: the a11y
       plugin below sets `visit.scroll.animate = false` alongside
       `visit.animation.animate` whenever the query matches. Measured -- under
       `reduce` the page jumps rather than glides.

       doScrollingRightAway, so the scroll starts with the fade rather than
       after it. The alternative -- scrolling once the new markup is in --
       shows the incoming page travelling upwards, which reads as the page
       having loaded in the wrong place. */
    new SwupScrollPlugin({ doScrollingRightAway: true }),

    /* headingSelector is left at its default. The h1 is in <header>, not in
       <main>, so the plugin's first choice misses and its second finds it. */
    new SwupA11yPlugin({ announcements }),

    new SwupPreloadPlugin({ preloadHoveredLinks: true }),

    new SwupProgressPlugin({
      className: 'swup-progress-bar',
      delay: 300,
      initialValue: 0.25,
      hideImmediately: true
    })
  ]
})

/* THE LANGUAGE SWITCH GOES THROUGH SWUP, and this hook is the price.
   Ben's ask of 2026-09-19: it was the one link on the site that reloaded, and
   the missing transition was visible next to every other link.
   langSwitchTpl's `data-no-swup` is gone with this.
 
   It was excluded for two concrete reasons, and both are about things that sit
   OUTSIDE every container and are ATTRIBUTES, so no amount of container
   juggling reaches them:
 
     <html lang>          the document language. Left at `de` on an English
                          page that is WCAG 3.1.1, and it is not cosmetic --
                          `hyphens: auto` in base/_fonts.scss breaks words
                          against the document's dictionary, so a stale lang
                          hyphenates English prose with German rules.
     <body data-announce> the a11y plugin's announcement strings, which the
                          server writes per language. Stale, the next visit is
                          announced in the language the visitor just left.
 
   Both are copied from the incoming document here. `visit.to.document` is a
   parsed Document -- Swup fills it when the fetch resolves and DELETES it in
   the `finally` of performNavigation, so it can only be read during the visit.
   `content:replace` is inside that window and before the announcement, which
   the plugin makes on page:view.
 
   Swup's own replaceContent copies the <title> from the incoming document for
   exactly the same reason. This is the same move for the two attributes it
   does not know about.

   The list was right and it was SHORT. Finding N1 of 2026-09-20 found the
   markup out there too -- the skip link and the whole footer -- which is
   markup rather than attributes and is now carried by making both
   containers. What is left for this hook is those two attributes and the one
   element a container cannot express, below. */
/* THE HERO'S HEIGHT, carried across the swap so it animates instead of
   snapping. Backlog E44.

   --hero-height is min(85vh, 68rem) on the home page and min(70vh, 56rem)
   elsewhere, so leaving or entering the home page changes the header's height.
   `header` has `transition: all var(--motion-slow)` and that transition CANNOT
   FIRE on a visit, because `header` is a container: it is replaced, and a
   newly inserted element has no previous computed height to animate from.
   Measured before this was written -- 10 visits out of 10 snapped, 0
   intermediate values, in both directions.

   Ben reported it as sometimes animating and sometimes not, with no pattern.
   That is consistent: the transition fires only when something changes the
   height AFTER insertion -- the body-class swap landing in a later frame, a
   font finishing, the video's metadata arriving -- which is a race. THE
   ANIMATED CASE WAS NEVER REPRODUCED HERE, so the inconsistency is explained
   from the mechanism rather than measured.

   The outgoing height is read while the old markup is still in the document,
   then written inline onto the incoming header and released on the SECOND
   frame. One frame is not enough: the style has to be applied and rendered
   before it is removed, or the browser coalesces the two and there is nothing
   to transition between. Measured: 6 of 6 animated, 26 intermediate values.

   NO GUARD ON WHETHER THE HEIGHT ACTUALLY DIFFERS, deliberately. Reading the
   incoming header's natural height here would mean reading it before or after
   BodyClassPlugin swaps the class that decides it, depending on hook
   registration order, and a guard that is sometimes comparing against the old
   page's value would skip the animation on exactly the visits that need it.
   Setting an inline height that equals the natural one costs two frames of
   nothing. */
let outgoingHeaderHeight = null

swup.hooks.before('content:replace', () => {
  const header = document.querySelector('header')
  outgoingHeaderHeight = header ? header.getBoundingClientRect().height : null
})

swup.hooks.on('content:replace', (visit) => {
  const header = document.querySelector('header')
  if (!header) return

  /* Any inline height left behind by an interrupted visit, before anything
     reads a height. A stuck inline value would pin the hero for the rest of
     the session. */
  header.style.height = ''

  /* `visit.animation.animate` is what the a11y plugin sets to false under
     prefers-reduced-motion -- the same flag it uses to stop the scroll from
     gliding. Under `reduce` the height snaps, which is the correct behaviour
     and not a degradation. */
  if (!visit.animation.animate || outgoingHeaderHeight == null) return

  header.style.height = `${outgoingHeaderHeight}px`
  requestAnimationFrame(() => requestAnimationFrame(() => {
    header.style.height = ''
  }))
})

swup.hooks.on('content:replace', (visit) => {
  const incoming = visit.to.document
  if (!incoming) return

  const lang = incoming.documentElement.lang
  if (lang) document.documentElement.lang = lang

  /* BodyClassPlugin carries the class attribute and nothing else, and <body>
     is not a container, so its data-* survive the swap unchanged. */
  const { announce, announceUrl } = incoming.body.dataset
  document.body.dataset.announce = announce ?? ''
  document.body.dataset.announceUrl = announceUrl ?? ''

  registerAnnouncements(incoming)

  /* THE CALL BUTTON, because its PRESENCE varies and a container cannot say
     so. callButton.chunk renders no pill on a `hidemenu` page -- /impressum
     is the one published example -- and it is called from htmlFooter, which
     template 2 does not use. A container is only ever a replacement: Swup 4
     looks the selector up in both documents and, finding it in one, warns and
     skips (verified in node_modules/swup/dist/Swup.modern.js, not assumed).

     As a container it would therefore have fixed nothing and broken two
     things. Both were measured at 390px before this was written, and both
     PREDATE it -- neither is part of N1:

       / -> /impressum   the pill is in the current document and not in the
                         incoming one, so nothing replaces it and it stays
                         VISIBLE on the one page that deliberately has none.
       /impressum -> /   entering there directly and then navigating, the pill
                         is missing from the CURRENT document, so Swup skips
                         the container and the pill NEVER APPEARS -- on any
                         page, for the rest of the session.

     The second is the one that matters. _call-button.scss calls this "the one
     control that has to be reachable without scrolling, on every page, at all
     times" and ties it to the Hochzeitsmesse; a visitor who lands on the
     Impressum from a QR code and then browses the site has no telephone CTA
     at all, and the telephone carries the whole conversion.

     So the incoming document decides, in all three directions: replace,
     insert, remove. Cloned rather than moved, which is what Swup's own
     replaceContent does -- `visit.to.document` is deleted in the `finally` of
     performNavigation and the a11y plugin reads from it after this hook.
     Appended rather than inserted in place, because callButton.chunk puts the
     pill last in <body> on purpose: the last tab stop, rather than something
     between the skip link and the navigation.

     This carries the `aria-label` as a side effect, which is the fifth of
     N1's six stale fields and the only reason the call button was in that
     finding at all. */
  const pill = document.querySelector('.call-button')
  const incomingPill = incoming.querySelector('.call-button')
  if (incomingPill) {
    const fresh = incomingPill.cloneNode(true)
    pill ? pill.replaceWith(fresh) : document.body.append(fresh)
  } else {
    pill?.remove()
  }
})

/* MOVING WITHIN A SECTION IS NOT LEAVING THE PAGE, Ben 2026-09-21.

   The four pages of a retired section share a strip, and the main navigation's
   entry for the section is the same link on all four. A click inside that
   strip is a navigation -- E46's whole point -- but it is the one navigation on
   this site where almost nothing about the frame changes, and treating it like
   any other visit made it read as a page load: the view jumped to the top, and
   the current-page line under `philosophie` collapsed and reopened onto itself.

   THE PREDICATE IS THE STRIP'S OWN LINKS. A visit stays inside the section when
   this page has a strip and the target is one of the pages it lists. Nothing
   server-side has to say so, and it cannot drift from what the reader sees,
   because the strip IS the list of what counts as inside.

   It answers false for a language switch, which lands on the English twin --
   not a URL this strip names -- and that is correct: the frame really does
   change there. */
const STAYING = 'is-staying'

const staysInSection = (url) => {
  const strip = document.querySelector('nav.section-nav')
  if (!strip) return false

  const target = new URL(url, window.location.origin).pathname
  return [...strip.querySelectorAll('a[href]')]
    .some((link) => new URL(link.href, window.location.origin).pathname === target)
}

swup.hooks.on('visit:start', (visit) => {
  if (!staysInSection(visit.to.url)) return

  /* The scroll plugin's own flag, rather than a competing scrollTo: the reader
     stays where they were reading. The strip is above the fold on every one of
     these pages, so the control they just used is still on screen. */
  visit.scroll.reset = false

  /* Read by the two departure rules -- components/_site-nav.scss for the main
     navigation's line and components/_section-nav.scss for the strip's. Both
     collapse their line on `is-leaving`, which is right when the reader is
     leaving the thing the line marks and wrong here: the navigation's line
     marks a section the reader is staying in, and the strip's simply moves to
     another item. It is a class on <html> rather than a state in either
     stylesheet because Swup's own `is-leaving` is, and the two have to be read
     together. */
  document.documentElement.classList.add(STAYING)
})

/* At visit:end rather than content:replace: `is-leaving` comes off before the
   swap, so a flag that has to outlast it cannot be removed at the same moment.
   Also on an aborted visit, which is what visit:end is for. */
swup.hooks.on('visit:end', () => {
  document.documentElement.classList.remove(STAYING)
})
